// src/share/TeamSharePreview.jsx
// Platform-specific team share preview frames for the Sharing Center.
// Shows 1-5 small member photos, team name, and city/area.

import React from 'react';
import CustomerPicker from './CustomerPicker';
import { parseEmailList } from './parseEmailList';
import { XLinkCardFrame, WhatsAppMessageFrame } from './IntentShareFrames';

const API_URL = process.env.REACT_APP_API_URL || '';

// See CompanySharePreview.jsx — same flag, same purpose.
const X_API_ENABLED = process.env.REACT_APP_X_API_ENABLED === 'true';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function makeHandle(name) {
  if (!name) return 'yourbusiness';
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 20);
}

function makeInitials(name) {
  if (!name) return 'Biz';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function BrandingStrip({ light = false }) {
  return (
    <div className={`flex items-center justify-center py-1.5 ${light ? 'text-white/70' : 'text-[#00aef7]/60'}`}>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.05em' }}>Shared through eHandshake</span>
    </div>
  );
}

// ─── Star rating row ─────────────────────────────────────────────────────────
function Stars({ avg, count }) {
  if (!avg && !count) return null;
  const score = (avg || 0).toFixed(2);
  const full = Math.floor(avg || 0);
  const half = (avg || 0) - full >= 0.25;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex items-center">
        {Array.from({ length: full }).map((_, i) => (
          <span key={`f${i}`} className="text-amber-400" style={{ fontSize: 'inherit' }}>&#9733;</span>
        ))}
        {half && <span className="text-amber-400" style={{ fontSize: 'inherit' }}>&#9733;</span>}
        {Array.from({ length: empty }).map((_, i) => (
          <span key={`e${i}`} className="text-gray-300" style={{ fontSize: 'inherit' }}>&#9733;</span>
        ))}
      </div>
      <div className="font-bold text-gray-800 mt-0.5" style={{ fontSize: 'inherit' }}>{score}</div>
      {count > 0 && <div className="text-gray-400" style={{ fontSize: '0.85em' }}>{count} review{count === 1 ? '' : 's'}</div>}
    </div>
  );
}

// ─── Small member avatar circle ──────────────────────────────────────────────
function MemberAvatar({ src, name, size = 36 }) {
  const initials = name
    ? name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';
  return (
    <div
      className="rounded-full overflow-hidden border-2 border-white shadow-sm bg-gray-100 shrink-0"
      style={{ width: size, height: size }}
      title={name || ''}
    >
      {src ? (
        <img src={`${API_URL}${src}`} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200" style={{ fontSize: Math.max(8, size * 0.32) }}>
          {initials}
        </div>
      )}
    </div>
  );
}

// ─── Derive team location from members ───────────────────────────────────────
function deriveTeamLocation(members) {
  if (!members || members.length === 0) return '';
  // Use the most common city/state combo
  const counts = {};
  members.forEach(m => {
    const loc = [m.b_city, m.b_state].filter(Boolean).join(', ');
    if (loc) counts[loc] = (counts[loc] || 0) + 1;
  });
  const entries = Object.entries(counts);
  if (entries.length === 0) return '';
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TeamShareCard — 1-5 member photos, team name, city/area
// ═══════════════════════════════════════════════════════════════════════════════

function TeamShareCard({ team, aspect = '1.91/1' }) {
  const teamName = team.team_name || 'Team';
  const members = team.members || [];
  const displayMembers = members.slice(0, 5);
  const extraCount = members.length > 5 ? members.length - 5 : 0;
  const location = deriveTeamLocation(members);
  const memberCount = members.length;

  // Compute team score: weighted average across all members
  let teamTotalScore = 0;
  let teamTotalReviews = 0;
  members.forEach(m => {
    const avg = m.rating_avg ? Number(m.rating_avg) : 0;
    const cnt = m.rating_count ? Number(m.rating_count) : 0;
    if (avg > 0 && cnt > 0) {
      teamTotalScore += avg * cnt;
      teamTotalReviews += cnt;
    }
  });
  const teamScore = teamTotalReviews > 0 ? Math.round((teamTotalScore / teamTotalReviews) * 10) / 10 : 0;

  const isTall = aspect === '9/16';
  const isShort = aspect === '1.91/1';

  const avatarSize = isTall ? 44 : isShort ? 30 : 38;

  return (
    <div
      className="relative w-full overflow-hidden flex flex-col"
      style={{
        aspectRatio: aspect,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
      }}
    >
      <div className="h-1 shrink-0" style={{ background: 'linear-gradient(90deg, #00aef7, #0077cc)' }} />

      {/* All layouts use centered vertical: photos on top, text below */}
      <div className={`flex-1 flex flex-col items-center justify-center px-4 ${isShort ? 'py-1.5' : 'py-3'} text-center`}>
        {/* Member photo row */}
        <div className={`flex items-center justify-center ${isShort ? 'mb-1.5' : 'mb-3'}`} style={{ gap: -4 }}>
          {displayMembers.map((m, i) => {
            const mName = [m.u_first_name, m.u_last_name].filter(Boolean).join(' ');
            return (
              <div key={m.u_id || i} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: displayMembers.length - i }}>
                <MemberAvatar src={m.u_profile_picture} name={mName} size={avatarSize} />
              </div>
            );
          })}
        </div>
        {extraCount > 0 && (
          <div className="text-gray-500 font-medium" style={{ fontSize: isTall ? 12 : isShort ? 9 : 11, marginTop: isShort ? 2 : 4 }}>
            +{extraCount} more
          </div>
        )}

        <div className={isShort ? 'space-y-0.5' : 'space-y-1'}>
          <div className="font-bold text-gray-900" style={{ fontSize: isTall ? 16 : isShort ? 13 : 14 }}>{teamName}</div>
          <div className="text-gray-500" style={{ fontSize: isTall ? 12 : isShort ? 10 : 11 }}>
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
          </div>
          {location && (
            <div className="text-gray-400 flex items-center justify-center gap-0.5" style={{ fontSize: isTall ? 11 : isShort ? 9 : 10 }}>
              <span>&#128205;</span> {location}
            </div>
          )}
          {teamScore > 0 && (() => {
            const tFull = Math.floor(teamScore);
            const tHalf = teamScore - tFull >= 0.25;
            const tEmpty = 5 - tFull - (tHalf ? 1 : 0);
            const fs = isTall ? 11 : isShort ? 9 : 10;
            return (
              <div className="flex flex-col items-center pt-0.5" style={{ fontSize: fs }}>
                <div className="flex items-center">
                  {Array.from({ length: tFull }).map((_, i) => <span key={`f${i}`} className="text-amber-400">&#9733;</span>)}
                  {tHalf && <span className="text-amber-400">&#9733;</span>}
                  {Array.from({ length: tEmpty }).map((_, i) => <span key={`e${i}`} className="text-gray-300">&#9733;</span>)}
                </div>
                <div className="font-bold text-gray-800 mt-0.5" style={{ fontSize: fs }}>{teamScore.toFixed(2)}</div>
                {teamTotalReviews > 0 && <div className="text-gray-400" style={{ fontSize: fs * 0.85 }}>{teamTotalReviews} review{teamTotalReviews === 1 ? '' : 's'}</div>}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="shrink-0"><BrandingStrip /></div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Platform Frame Components
// ═══════════════════════════════════════════════════════════════════════════════

function TeamFacebookFrame({ team, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">Just now &middot; &#127760;</div>
        </div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="10" cy="16" r="2"/></svg>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <TeamShareCard team={team} aspect="1.91/1" />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5"><span className="text-[12px]">&#128077;</span><span className="text-[12px]">&#10084;&#65039;</span><span>24</span></div>
        <div>3 comments &middot; 2 shares</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128077; Like</div>
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128172; Comment</div>
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#8599;&#65039; Share</div>
      </div>
    </div>
  );
}

function TeamInstagramFrame({ team, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[300px] mx-auto">
      <div className="px-2.5 py-1.5 flex items-center gap-2 border-b border-gray-100">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[8px] font-bold text-gray-700">{biz.initials}</div>
        </div>
        <div className="flex-1 min-w-0"><div className="text-[11px] font-semibold text-gray-900">{biz.handle}</div></div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="4" cy="10" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="16" cy="10" r="2"/></svg>
      </div>
      <TeamShareCard team={team} aspect="1/1" />
      <div className="px-2.5 py-1.5 flex items-center gap-3">
        <span className="text-[15px]">&#129293;</span>
        <span className="text-[15px]">&#128172;</span>
        <span className="text-[15px]">&#128228;</span>
        <span className="ml-auto text-[15px]">&#128278;</span>
      </div>
      <div className="px-2.5 text-[11px] font-semibold text-gray-900">127 likes</div>
      <div className="px-2.5 pb-2.5 pt-0.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap"><span className="font-semibold">{biz.handle} </span>{caption}</p>
      </div>
    </div>
  );
}

function TeamYouTubeFrame({ team, caption, biz }) {
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden max-w-[210px] mx-auto" style={{ aspectRatio: '9/16' }}>
      <div className="relative w-full h-full">
        <TeamShareCard team={team} aspect="9/16" />
        <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128077;</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128078;</div>
            <span className="text-white text-[8px] mt-0.5">Dislike</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128172;</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128228;</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
        <div className="absolute top-2 left-2 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">Shorts</div>
      </div>
    </div>
  );
}

function TeamLinkedInFrame({ team, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">{biz.location || 'Local Business'}</div>
          <div className="text-[10px] text-gray-400">Just now &middot; &#127760;</div>
        </div>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <TeamShareCard team={team} aspect="1.91/1" />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5"><span className="text-[12px]">&#128077;</span><span className="text-[12px]">&#128161;</span><span className="text-[12px]">&#10084;&#65039;</span><span className="ml-1">48</span></div>
        <div>6 comments</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128077; Like</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128172; Comment</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128260; Repost</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128228; Send</div>
      </div>
    </div>
  );
}

// X intent-URL preview — link card with the team share image cropped to 1.91:1.
function TeamXIntentFrame({ team, caption, biz }) {
  return (
    <XLinkCardFrame biz={biz} caption={caption}>
      <TeamShareCard team={team} aspect="1.91/1" />
    </XLinkCardFrame>
  );
}

// WhatsApp intent-URL preview — outgoing chat bubble with link unfurl.
function TeamWhatsAppFrame({ team, caption, biz }) {
  return (
    <WhatsAppMessageFrame biz={biz} caption={caption}>
      <TeamShareCard team={team} aspect="1.91/1" />
    </WhatsAppMessageFrame>
  );
}

function TeamTwitterFrame({ team, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1 flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-[9px] font-bold shrink-0">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-bold text-gray-900">{biz.name}</span>
            <span className="text-[10px] text-gray-500">@{biz.handle} &middot; Just now</span>
          </div>
          <p className="text-[11px] text-gray-900 leading-snug whitespace-pre-wrap mt-0.5">{caption}</p>
        </div>
      </div>
      <div className="px-2.5 pb-1">
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <TeamShareCard team={team} aspect="16/9" />
        </div>
      </div>
      <div className="px-8 py-1.5 flex items-center justify-between text-gray-500 text-[11px]">
        <span className="flex items-center gap-0.5">&#128172; <span>5</span></span>
        <span className="flex items-center gap-0.5">&#128260; <span>12</span></span>
        <span className="flex items-center gap-0.5">&#129293; <span>47</span></span>
        <span className="flex items-center gap-0.5">&#128228;</span>
      </div>
    </div>
  );
}

function TeamTikTokFrame({ team, caption, biz }) {
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden max-w-[210px] mx-auto" style={{ aspectRatio: '9/16' }}>
      <div className="relative w-full h-full">
        <TeamShareCard team={team} aspect="9/16" />
        <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#129293;</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128172;</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128228;</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamEmailFrame({ team, caption, biz }) {
  const teamName = team.team_name || 'Team';
  const members = team.members || [];
  const photoMembers = members.slice(0, 4);
  const extraCount = members.length > 4 ? members.length - 4 : 0;
  const directoryMembers = members.slice(0, 4);
  const location = deriveTeamLocation(members);
  const memberCount = members.length;

  // Compute team score
  let teamTotalScore = 0;
  let teamTotalReviews = 0;
  members.forEach(m => {
    const avg = m.rating_avg ? Number(m.rating_avg) : 0;
    const cnt = m.rating_count ? Number(m.rating_count) : 0;
    if (avg > 0 && cnt > 0) {
      teamTotalScore += avg * cnt;
      teamTotalReviews += cnt;
    }
  });
  const teamScore = teamTotalReviews > 0 ? Math.round((teamTotalScore / teamTotalReviews) * 10) / 10 : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[400px] mx-auto p-5">
      {/* Intro text */}
      <p className="text-[13px] text-gray-700 mb-3">
        <strong>{biz.name}</strong> wants to introduce you to their team.
      </p>
      {caption && <p className="text-[12px] text-gray-500 mb-4 whitespace-pre-line">{caption}</p>}

      {/* Card — matches sent email */}
      <div className="max-w-[340px] mx-auto rounded-lg overflow-hidden border border-gray-200" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)' }}>
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #00aef7, #0077cc)' }} />
        <div className="px-5 py-4 text-center">
          {/* Photo row — single row, overlapping */}
          <div className="flex items-center justify-center mb-2.5">
            {photoMembers.map((m, i) => {
              const mName = [m.u_first_name, m.u_last_name].filter(Boolean).join(' ');
              const compName = m.b_name || '';
              const tooltip = compName ? `${mName} - ${compName}` : mName;
              return (
                <div key={m.u_id || i} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: photoMembers.length - i }} title={tooltip}>
                  <MemberAvatar src={m.u_profile_picture} name={mName} size={40} />
                </div>
              );
            })}
            {extraCount > 0 && (
              <div
                className="rounded-full bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center text-gray-600 font-bold shrink-0"
                style={{ width: 40, height: 40, marginLeft: -8, zIndex: 0, fontSize: 11 }}
              >
                +{extraCount}
              </div>
            )}
          </div>

          <div className="font-bold text-[14px] text-gray-900 mb-0.5">{teamName}</div>
          <div className="text-[11px] text-gray-500 mb-0.5">
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
          </div>
          {location && <div className="text-[9px] text-gray-400 mb-0.5">📍 {location}</div>}

          {teamScore > 0 && (
            <div className="mt-1" style={{ fontSize: 10 }}>
              <Stars avg={teamScore} count={teamTotalReviews} />
            </div>
          )}

          {/* Member directory */}
          <div className="mt-2 space-y-0.5 text-center">
            {directoryMembers.map((m, i) => {
              const mName = [m.u_first_name, m.u_last_name].filter(Boolean).join(' ');
              const compName = m.b_name || '';
              return (
                <div key={m.u_id || i} className="text-[10px] text-gray-500">
                  {compName ? <><span className="text-[#00aef7]">{compName}</span> - <span className="text-[#00aef7]">{mName}</span></> : <span className="text-[#00aef7]">{mName}</span>}
                </div>
              );
            })}
          </div>

          {members.length > 4 && (
            <div className="mt-1.5">
              <span className="inline-block text-[10px] font-semibold text-[#00aef7] px-3 py-1 border border-[#00aef7] rounded">See More</span>
            </div>
          )}
        </div>
        {/* Branding strip */}
        <div className="text-center py-1.5 border-t border-blue-50">
          <span className="text-[#00aef7]/60" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.05em' }}>Shared through eHandshake</span>
        </div>
      </div>

      {/* CTA button */}
      <div className="text-center mt-4">
        <span className="inline-block px-6 py-2 bg-[#00aef7] text-white text-[12px] font-semibold rounded-md">
          View Team Profile
        </span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TeamShareModal
// ═══════════════════════════════════════════════════════════════════════════════

const PLATFORM_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  twitter: 'X / Twitter',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

function TeamShareModal({ team, platform, biz, socialConnections, fetchSocialStatus, onClose, onShare }) {
  const teamName = team?.team_name || 'Team';
  const [caption, setCaption] = React.useState('Check out our amazing team on eHandshake!');
  const [emailTo, setEmailTo] = React.useState('');
  const [emailSubject, setEmailSubject] = React.useState(`Meet the ${teamName} team on eHandshake`);
  // Customer multi-select for the email path. Adds stored customers to the
  // recipient list alongside the manual "To:" input.
  const [selectedCustomers, setSelectedCustomers] = React.useState([]);
  const [sending, setSending] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [postError, setPostError] = React.useState('');
  const oauthPopupRef = React.useRef(null);
  const postAfterConnectRef = React.useRef(false);

  React.useEffect(() => {
    if (!connecting) return;
    const interval = setInterval(() => {
      if (oauthPopupRef.current?.closed) {
        clearInterval(interval);
        setConnecting(false);
        if (fetchSocialStatus) {
          fetchSocialStatus().then(() => {
            if (postAfterConnectRef.current) {
              postAfterConnectRef.current = false;
              doPost();
            }
          });
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [connecting]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!team || !platform) return null;

  const FrameComp = TEAM_FRAME_MAP[platform];
  if (!FrameComp) return null;

  const isEmail = platform === 'email';
  const platformLabel = PLATFORM_LABELS[platform] || platform;
  const conn = socialConnections?.[platform];
  const isConnected = conn?.connected;
  // Intent-share platforms — see ProfileSharePreview for full explanation.
  const isWhatsApp = platform === 'whatsapp';
  const isXIntent = platform === 'twitter' && !X_API_ENABLED;
  const isIntentShare = isWhatsApp || isXIntent;

  // Combine the typed-in "To:" emails (comma/semicolon/space-separated)
  // with any selected customer emails.
  const allEmails = isEmail ? [
    ...parseEmailList(emailTo),
    ...selectedCustomers.map((c) => c.email),
  ] : [];
  const hasEmailRecipients = allEmails.length > 0;

  const doPost = async () => {
    setSending(true);
    setPostError('');
    try {
      if (onShare) {
        const finalEmailTo = isEmail ? allEmails.join(',') : emailTo;
        await onShare({ team, platform, caption, emailTo: finalEmailTo, emailSubject, shareType: 'team' });
      }
      onClose();
    } catch (err) {
      console.error('[TeamShareModal] share error:', err);
      setPostError(err.message || 'Failed to share. Please try again.');
      if (fetchSocialStatus) fetchSocialStatus();
    }
    setSending(false);
  };

  const handlePost = () => {
    setPostError('');
    if (isEmail || isConnected || isIntentShare) {
      // Intent-share platforms (WhatsApp, free-X) skip the OAuth popup.
      // Parent's onShare handler routes them to the picker / wa.me flow.
      doPost();
    } else {
      postAfterConnectRef.current = true;
      setConnecting(true);
      const w = 600, h = 700;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      oauthPopupRef.current = window.open(
        `${API_URL}/api/social/${platform}/connect`,
        'social-connect',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center pointer-events-auto" style={{ zIndex: 100000 }}>
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden relative"
        style={{ width: isEmail ? 560 : 440, maxWidth: '94vw', maxHeight: '90vh' }}
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800">
              {isEmail ? `Email ${teamName} Team Profile` : `Share ${teamName} on ${platformLabel}`}
            </h3>
            {!isEmail && isConnected && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const btn = e.currentTarget;
                    if (btn.dataset.confirm !== 'yes') {
                      btn.dataset.confirm = 'yes'; btn.textContent = 'Sure?';
                      btn.className = 'ml-1 text-[10px] text-red-500 font-semibold underline transition-colors';
                      setTimeout(() => { btn.dataset.confirm = ''; btn.textContent = 'Disconnect'; btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors'; }, 3000);
                      return;
                    }
                    btn.disabled = true; btn.textContent = 'Disconnecting…';
                    try {
                      await fetch(`${API_URL}/api/social/${platform}/disconnect`, { method: 'DELETE', credentials: 'include' });
                      if (fetchSocialStatus) fetchSocialStatus();
                    } catch (err) { console.error('disconnect error:', err); }
                    finally { btn.disabled = false; btn.dataset.confirm = ''; btn.textContent = 'Disconnect'; btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors'; }
                  }}
                  className="ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors"
                >Disconnect</button>
              </span>
            )}
            {!isEmail && !isConnected && !isIntentShare && (
              <span className="text-[10px] text-gray-400">Not connected</span>
            )}
            {isIntentShare && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                {isWhatsApp ? 'Opens WhatsApp' : 'Opens X'}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none" title="Close">&#10005;</button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
          {isEmail ? (
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                <input
                  type="text"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="email@example.com, another@example.com"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              {/* Customer picker — adds stored customers to the recipient list */}
              <CustomerPicker
                selectedCustomers={selectedCustomers}
                onChange={setSelectedCustomers}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={4}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Preview</label>
                <div className="flex justify-center">
                  <FrameComp team={team} caption={caption} biz={biz} />
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Your message</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={3}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm resize-none"
                  placeholder="Write something about this team..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">{platformLabel} Preview</label>
                <div className="flex justify-center">
                  <FrameComp team={team} caption={caption} biz={biz} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Connecting / Posting overlay */}
        {(connecting || sending) && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50 rounded-xl">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#00aef7] rounded-full animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-600">
              {connecting ? `Connecting to ${platformLabel}…` : `Posting to ${platformLabel}…`}
            </p>
            <p className="text-xs text-gray-400 mt-1">Please wait</p>
          </div>
        )}

        <div className="shrink-0 px-5 py-3 border-t border-gray-200 bg-gray-50">
          {postError && <p className="text-xs text-red-500 mb-2">{postError}</p>}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              disabled={sending || connecting}
            >
              Cancel
            </button>
            <button
              onClick={handlePost}
              disabled={sending || connecting || (isEmail && !hasEmailRecipients)}
              className={`px-5 py-2 text-white text-sm font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isWhatsApp ? 'bg-[#25D366] hover:bg-[#1FB958]'
                  : isXIntent ? 'bg-black hover:bg-gray-800'
                    : isConnected || isEmail ? 'bg-[#00aef7] hover:bg-[#0095d6]'
                      : 'bg-orange-500 hover:bg-orange-600'
              }`}
            >
              {sending
                ? (isEmail ? 'Sending...' : 'Opening…')
                : isEmail
                  ? 'Send Email'
                  : isIntentShare
                    ? `Share to ${platformLabel}`
                    : isConnected
                      ? `Post to ${platformLabel}`
                      : `Connect & Post to ${platformLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Exports
// ═══════════════════════════════════════════════════════════════════════════════

export const TEAM_FRAME_MAP = {
  facebook: TeamFacebookFrame,
  instagram: TeamInstagramFrame,
  youtube: TeamYouTubeFrame,
  linkedin: TeamLinkedInFrame,
  // X: native-tweet mockup when API tier is paid; link-card mockup otherwise.
  twitter: X_API_ENABLED ? TeamTwitterFrame : TeamXIntentFrame,
  tiktok: TeamTikTokFrame,
  whatsapp: TeamWhatsAppFrame,
  email: TeamEmailFrame,
};

export { TeamShareModal };
